import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchClientDashboard } from "../../Redux/Thunks/clientsThunk";
import {
  clearClientDashboard,
  selectClientDashboardData,
  selectClientDashboardLoading,
  selectClientDashboardError,
} from "../../Redux/Slices/clientDashboardSlice";
import Loader from "../../Utility/Loader/Loader";
import NoDataFound from "../../Utility/NoDataFound/NoDataFound";
import Typography from "../../Utility/Typography";
import Card from "../../Utility/Card/Card";
import PendingTasks from "../Dashboard/PendingTasks";
import TeamWorkload from "../Dashboard/TeamWorkload";
import EpicProgress from "../Dashboard/EpicProgress";
import ProjectStatistics from "../Projects/project-detail/dashboard/ProjectStatistics";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import { pickChangeTimestamp, formatDashboardChangeTime } from "../../utils/dashboardPayload";
import { formatEnumLabelForDisplay } from "../../utils/formatEnumLabelForDisplay";

/** Colors for pie / bar chart segments (status, priority, type). */
const CHART_COLORS = [
  "#22c55e",
  "#f97316",
  "#2563eb",
  "#9333ea",
  "#64748b",
  "#0ea5e9",
  "#a855f7",
  "#cd2c51fc",
  "#09b570",
];

function colorAt(i) {
  return CHART_COLORS[i % CHART_COLORS.length];
}

function activityTitle(type, metaObj) {
  if (type === "TIME_LOG_ADDED") {
    const work = [metaObj.workItemKey, metaObj.workItemTitle].filter(Boolean).join(" · ");
    const mins = metaObj.minutesLogged != null ? `${metaObj.minutesLogged}m` : "";
    return ["Time logged", work, mins].filter(Boolean).join(" — ");
  }
  if (type === "ISSUE_UPDATED") {
    const work = [metaObj.workItemKey, metaObj.workItemTitle].filter(Boolean).join(" · ");
    return ["Issue updated", work].filter(Boolean).join(" — ");
  }
  return String(type).replace(/_/g, " ");
}

/** White panel: title row + body (charts / lists). */
function DashboardPanel({ title, headerRight, children, bodyClassName = "p-4 md:p-5" }) {
  return (
    <div className="flex flex-col bg-white border border-gray-200 shadow-lg card-border">
      <div className="px-4 py-2 border-b border-gray-200 rounded-tl-[50px] rounded-tr-[20px] flex items-center justify-between min-h-[48px]">
        <Typography variant="h4">{title}</Typography>
        {headerRight}
      </div>
      <div className={bodyClassName}>{children}</div>
    </div>
  );
}

const ClientDashboard = ({ client, refreshToken }) => {
  const dispatch = useDispatch();
  const clientDashboardData = useSelector(selectClientDashboardData);
  const loading = useSelector(selectClientDashboardLoading);
  const error = useSelector(selectClientDashboardError);

  const [selectedTeam, setSelectedTeam] = useState("All");

  useEffect(() => {
    if (!client?.id) {
      dispatch(clearClientDashboard());
      return;
    }
    dispatch(fetchClientDashboard(client.id));
  }, [client?.id, dispatch, refreshToken]);

  const metrics = clientDashboardData?.metrics ?? {};
  const issues = clientDashboardData?.issues ?? {};
  const workload = clientDashboardData?.workload ?? {};
  const epics = clientDashboardData?.epics ?? [];

  const statusChartData = useMemo(
    () =>
      (issues.byStatus ?? []).map((s, i) => {
        const raw = s.statusName ?? s.category ?? "";
        const name = formatEnumLabelForDisplay(raw) || "Status";
        return {
          name,
          value: Number(s.count) || 0,
          color: colorAt(i),
        };
      }),
    [issues.byStatus]
  );

  const priorityChartData = useMemo(
    () =>
      (issues.byPriority ?? []).map((p, i) => ({
        name: formatEnumLabelForDisplay(String(p.priority ?? "PRIORITY")) || "Priority",
        value: Number(p.count) || 0,
        color: colorAt(i),
      })),
    [issues.byPriority]
  );

  const typeChartData = useMemo(
    () =>
      (issues.byType ?? []).map((t, i) => ({
        name: formatEnumLabelForDisplay(String(t.type ?? "TYPE")) || "Type",
        value: Number(t.count) || 0,
        color: colorAt(i),
      })),
    [issues.byType]
  );

  const activities = useMemo(() => {
    const rows = clientDashboardData?.activity ?? [];
    return rows.map((a) => {
      const by = a?.by ?? {};
      const metaObj = a?.meta ?? {};
      const type = a?.type ?? "ACTIVITY";
      return {
        id: a?.id ?? `${type}-${a?.at ?? ""}`,
        name: by?.name ?? by?.email ?? "User",
        title: activityTitle(type, metaObj),
        time: formatDashboardChangeTime(pickChangeTimestamp(a)),
      };
    });
  }, [clientDashboardData?.activity]);

  const teams = useMemo(
    () =>
      (workload.teams ?? []).map((t) => ({
        teamId: t.teamId,
        name: t.teamName ?? `Team ${t.teamId ?? ""}`.trim(),
        value: Number(t.workloadPercent) || 0,
        tasks: Number(t.tasks) || 0,
        projects: Number(t.projects) || 0,
      })),
    [workload.teams]
  );

  const teamMembersData = useMemo(() => {
    const src = workload.membersByTeam ?? {};
    const byName = {};
    for (const t of teams) {
      const matched = src[String(t.teamId)] ?? src[t.name] ?? [];
      byName[t.name] = (Array.isArray(matched) ? matched : []).map((m) => ({
        name: m.name ?? m.email ?? "Unknown",
        workload: Number(m.workloadPercent) || 0,
        tasks: Number(m.tasks) || 0,
        projects: Number(m.projects) || 0,
      }));
    }
    return byName;
  }, [workload.membersByTeam, teams]);

  const teamOptions = useMemo(() => ["All", ...teams.map((t) => t.name)], [teams]);

  if (error && !loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Typography variant="text1" className="text-red-600 text-lg">
          {error}
        </Typography>
      </div>
    );
  }

  if (loading) {
    return <Loader variant="fullScreen" />;
  }

  if (!clientDashboardData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <NoDataFound message="No dashboard data available" />
      </div>
    );
  }

  const maxHoursMonthly = Number(metrics.maxHoursMonthly) || 0;
  const usedHoursThisMonth = Number(metrics.usedHoursThisMonth) || 0;
  const remainingHoursThisMonth = Number(metrics.remainingHoursThisMonth) || 0;
  const percentUsed =
    maxHoursMonthly > 0 ? Math.round((usedHoursThisMonth / maxHoursMonthly) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        {/* Top row: KPI summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Active Projects */}
          <Card
            title="Active Projects"
            value={Number(metrics.activeProjects) || 0}
            bgColor="bg-dashboard-variant-1"
            className="rounded-lg"
          />
          {/* Open Tasks */}
          <Card
            title="Tasks"
            value={Number(metrics.openTasks) || 0}
            bgColor="bg-dashboard-variant-2"
            className="rounded-lg"
          />
          {/* Open Bugs */}
          <Card
            title="Bugs"
            value={Number(metrics.openBugs) || 0}
            bgColor="bg-dashboard-variant-3"
            className="rounded-lg"
          />
          {/* Hours logged this month */}
          <Card
            title="Hours Logged"
            value={usedHoursThisMonth.toLocaleString()}
            bgColor="bg-dashboard-variant-4"
            className="rounded-lg"
          />
          {/* Monthly hour budget + usage bar */}
          <Card
            title="Monthly Budget"
            bgColor="bg-dashboard-variant-5"
            className="rounded-lg"
            additionalContent={
              <>
                <Typography
                  variant="text2"
                  className="text-white text-xs opacity-80 mt-0 flex gap-1 m-0 p-0"
                >
                  Budget:{" "}
                  <Typography variant="text2" className="text-white text-xs m-0 p-0 font-bold">
                    {maxHoursMonthly} hrs
                  </Typography>
                </Typography>
                <Typography
                  variant="text2"
                  className="text-white text-xs opacity-80 flex gap-1 m-0 p-0 mt-1 mb-1"
                >
                  Remaining:{" "}
                  <Typography variant="text2" className="text-white text-xs m-0 p-0 font-bold">
                    {remainingHoursThisMonth} hrs
                  </Typography>
                </Typography>
                <div className="flex items-center gap-3 mt-0">
                  <div className="flex-1 w-full bg-white bg-opacity-30 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        percentUsed > 100
                          ? "bg-red-500"
                          : percentUsed > 80
                            ? "bg-yellow-500"
                            : "bg-green-500"
                      }`}
                      style={{ width: `${Math.min(percentUsed, 100)}%` }}
                    />
                  </div>
                  <Typography
                    variant="text2"
                    className="text-white text-xs font-medium whitespace-nowrap opacity-90 m-0"
                  >
                    {percentUsed}% used
                  </Typography>
                </div>
              </>
            }
          />
        </div>

        {/* Status chart + activity feed */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Issues by status */}
          <DashboardPanel title="Status Overview">
            <PendingTasks dataValues={statusChartData} />
          </DashboardPanel>
          {/* Recent timeline */}
          <DashboardPanel
            title="Recent Activity"
            bodyClassName="px-4 py-4 space-y-5 max-h-[350px] overflow-y-auto"
          >
            {activities.length === 0 ? (
              <Typography variant="text2" className="text-gray-500">
                No recent activity
              </Typography>
            ) : (
              activities.map((item) => (
                <div key={item.id} className="flex gap-3 items-center">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
                      {item.name
                        .split(" ")
                        .map((word) => word[0])
                        .join("")
                        .toUpperCase()}
                    </div>
                  </div>
                  <div className="flex-1 text-sm">
                    <Typography className="text-gray-800 leading-relaxed">
                      <Typography
                        variant="span3"
                        className="font-semibold text-green-600 mb-0"
                        component="span"
                      >
                        {item.name}
                      </Typography>
                      <Typography
                        variant="span2"
                        className="text-gray-800 mb-0 pl-1"
                        component="span"
                      >
                        {item.title}
                      </Typography>
                      {" - "}
                      {item.time?.trim() ? item.time : "—"}
                    </Typography>
                  </div>
                </div>
              ))
            )}
          </DashboardPanel>
        </div>

        {/* Priority + work type charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 mt-5">
          <DashboardPanel title="Priority Breakdown">
            <ProjectStatistics dataValues={priorityChartData} />
          </DashboardPanel>
          <DashboardPanel title="Types Of Work" bodyClassName="p-4 md:p-5 h-auto min-h-0">
            <ProjectStatistics dataValues={typeChartData} />
          </DashboardPanel>
        </div>

        {/* Team workload + epics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-5">
          <DashboardPanel
            title="Team Workload"
            headerRight={
              <div className="w-40">
                <Dropdown
                  name="teamWorkload"
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  options={teamOptions}
                  placeholder="Select Team"
                  noWrapper={true}
                  className="text-sm"
                />
              </div>
            }
          >
            <TeamWorkload
              selectedTeam={selectedTeam}
              teamsData={teams}
              teamMembersData={teamMembersData}
            />
          </DashboardPanel>
          <DashboardPanel
            title="Epic Progress"
            bodyClassName="px-4 py-4 space-y-5 max-h-[350px] overflow-y-auto"
            className="px-4 py-4 space-y-5 max-h-[350px] overflow-y-auto"
          >
            <EpicProgress epics={epics} />
          </DashboardPanel>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
